import React, { useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { TextInput, Select, Button, Checkbox, List, Alert } from '@pella/sash-framework';
import EloquaForm from '../../../common/eloqua-form';
import styles from './index.module.scss';
import { isValidEmail, isValidPostal } from '../../../utilities/validations';
import { eloquaTrackingUrl } from '../../../utilities/constants';

const EbookForm = ({ path, utm }) => {
	const [values, setValues] = useState({
		ebookFirstName: '',
		ebookLastName: '',
		ebookEmail: '',
		ebookBusinessName: '',
		ebookBusinessType: '',
		ebookBusinessZip: '',
		ebookPhone: '',
		ebookPermission: false,
	});
	const [errors, setErrors] = useState({});
	const [success, setSuccess] = useState(false);

	const validateForm = (ebookFirstName, ebookLastName, ebookEmail, ebookBusinessName, ebookBusinessType, ebookBusinessZip, ebookPhone) => {
		let errors = [];

		if (ebookFirstName.length === 0) {
			errors.ebookFirstName = 'Please enter your first name';
		}

		if (ebookLastName.length === 0) {
			errors.ebookLastName = 'Please enter your last name';
		}

		if (!isValidEmail(ebookEmail)) {
			errors.ebookEmail = 'Please enter a valid email address';
		}

		if (ebookBusinessName.length === 0) {
			errors.ebookBusinessName = 'Please enter a business name';
		}

		if (ebookBusinessType.length === 0) {
			errors.ebookBusinessType = 'Please select a business type';
		}

		if (!isValidPostal(ebookBusinessZip)) {
			errors.ebookBusinessZip = 'Please enter the business postal code';
		}

		if (ebookPhone.length === 0) {
			errors.ebookPhone = 'Please enter your phone number';
		}

		return errors;
	};

	const onFormSubmit = (e) => {
		e.preventDefault();
		const { ebookFirstName, ebookLastName, ebookEmail, ebookBusinessName, ebookBusinessType, ebookBusinessZip, ebookPhone } = values;
		const errors = validateForm(ebookFirstName, ebookLastName, ebookEmail, ebookBusinessName, ebookBusinessType, ebookBusinessZip, ebookPhone);

		if (Object.keys(errors).length > 0) {
			setErrors(errors);
		} else {
			const data = new FormData(e.target);
			setErrors({});

			axios
				.post(eloquaTrackingUrl, data)
				.then((res) => {
					if (res.status === 200) {
						setSuccess(true);
					}
				})
				.catch();
		}
	};

	const onInputChange = (e) => {
		setValues({
			...values,
			[e.target.name]: e.target.type === 'checkbox' ? e.target.checked : e.target.value,
		});
	};

	const onNumberInputChange = (name, value) => {
		setValues({
			...values,
			[name]: value,
		});
	};

	const onPdfClick = () => {
		window.open('https://assets.contentstack.io/v3/assets/bltf589e66bcaecd79c/blt2fb00f0f45c90bdb/Building-for-Success-ebook.pdf', '_blank');
	};

	const onEpubClick = () => {
		window.open('https://assets.contentstack.io/v3/assets/bltf589e66bcaecd79c/blt136b5fa77603de11/building-for-success.epub', '_blank');
	};

	return (
		<EloquaForm
			onFormSubmit={(e) => onFormSubmit(e)}
			elqFormName='pro-ebook'
			elqCookieWrite={1}
			utm={utm}
		>
			<input
				type='hidden'
				name='ebookReferrer'
				value={path}
			/>

			{success && (
				<>
					<Alert
						type='success'
						hideClose={true}
						divider={true}
						title='Thank you for your submission!'
						className='mb-4 text-left'
					>
						You have successfully signed up for your free ebook. Please use the links below to download the PDF or ePub version.
					</Alert>

					<div className='ebook-success-ctas'>
						<Button
							color='primary'
							rel='noopener noreferrer'
							title='This link will download a PDF E-book'
							className={styles.ebookForm_Btn}
							download
							onClick={() => onPdfClick()}
						>
							Open the PDF
						</Button>

						<Button
							color='primary'
							rel='noopener noreferrer'
							title='This link will download a ePub E-book'
							className={styles.ebookForm_Btn}
							download
							onClick={() => onEpubClick()}
						>
							Download e-Reader File
						</Button>
					</div>
				</>
			)}

			{!success && (
				<List bullets='none'>
					<li>
						<TextInput
							id='ebookFirstName'
							labelText='First Name'
							required={true}
							onChange={(e) => onInputChange(e)}
							error={errors.ebookFirstName ? true : false}
							errorText={errors.ebookFirstName}
							autoComplete='off'
						/>
					</li>

					<li>
						<TextInput
							id='ebookLastName'
							labelText='Last Name'
							required={true}
							onChange={(e) => onInputChange(e)}
							error={errors.ebookLastName ? true : false}
							errorText={errors.ebookLastName}
							autoComplete='off'
						/>
					</li>

					<li>
						<TextInput
							type='email'
							id='ebookEmail'
							labelText='Email Address'
							required={true}
							onChange={(e) => onInputChange(e)}
							error={errors.ebookEmail ? true : false}
							errorText={errors.ebookEmail}
							autoComplete='off'
						/>
					</li>

					<li>
						<TextInput
							id='ebookBusinessName'
							labelText='Business Name'
							required={true}
							onChange={(e) => onInputChange(e)}
							error={errors.ebookBusinessName ? true : false}
							errorText={errors.ebookBusinessName}
							autoComplete='off'
						/>
					</li>

					<li>
						<TextInput
							id='ebookBusinessZip'
							labelText='Business Zip'
							required={true}
							onChange={(e) => onInputChange(e)}
							error={errors.ebookBusinessZip ? true : false}
							errorText={errors.ebookBusinessZip}
							maxLength={5}
							autoComplete='off'
						/>
					</li>

					<li>
						<Select
							id='ebookBusinessType'
							labelText='Business Type'
							required={true}
							onChange={(e) => onInputChange(e)}
							error={errors.ebookBusinessType ? true : false}
							errorText={errors.ebookBusinessType}
							autoComplete='off'
						>
							<option value=''>--- Select Business Type ---</option>
							<option value='Replacement Contractor'>Replacement Contractor</option>
							<option value='Remodeling Contractor'>Remodeling Contractor</option>
							<option value='Residential Builder/GC'>Residential Builder/GC</option>
							<option value='Architectural / Design Firm'>Architectural / Design Firm</option>
							<option value='Commercial Builder/GC'>Commercial Builder/GC</option>
							<option value='Commercial Building Owner'>Commercial Building Owner</option>
							<option value='Homeowner'>Homeowner</option>
						</Select>
					</li>

					<li>
						<TextInput
							type='tel'
							id='ebookPhone'
							labelText='Phone'
							required
							onValueChange={(values) => onNumberInputChange('ebookPhone', values.formattedValue)}
							error={errors.ebookPhone ? true : false}
							errorText={errors.ebookPhone}
							format='(###) ###-####'
							allowEmptyFormatting
							autoComplete='off'
						/>
					</li>

					<li>
						<Checkbox
							id='ebookPermission'
							name='ebookPermission'
							labelText='Keep me updated on the latest products, news and updates from Pella'
							onChange={(e) => onInputChange(e)}
						/>
					</li>

					<li>
						<Button
							color='primary'
							type='submit'
							wide={true}
						>
							Get Your E-Book
						</Button>
					</li>
				</List>
			)}
		</EloquaForm>
	);
};

EbookForm.propTypes = {
	path: PropTypes.string,
	utm: PropTypes.object,
};

export default EbookForm;
